import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import type { SmbHomeImageStatus } from "@shared/smb-home-images";

type Slot = { binding: string; status: SmbHomeImageStatus; error: string | null; url: string | null; provenance: string; attempt?: string };
const busy = (slot: Slot) => ["planned", "prompt-submitting", "prompt-ready", "render-submitting", "submitted"].includes(slot.status);

/** Independent progress remains available when the owning page failed but its
 * copy is still usable. Neither status polling nor reload submits new work. */
export function SmbHomeImageProgress({ brandId, pageId, onRefresh }: {
  brandId: string; pageId: string; onRefresh: () => void;
}) {
  const queryClient = useQueryClient();
  const origin = `${brandId}:${pageId}`;
  const originRef = useRef(origin);
  originRef.current = origin;
  const refreshRef = useRef(onRefresh);
  const retrying = useRef(false);
  const retriedState = useRef<string | null>(null);
  refreshRef.current = onRefresh;
  const url = `/api/brands/${brandId}/lp-express/smb-home/${pageId}/images`;
  const query = useQuery<{ slots: Slot[] }>({
    queryKey: [url], queryFn: async () => {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Could not load saved image progress.");
      const data = await response.json();
      if (!Array.isArray(data?.slots)) throw new Error("Invalid saved image progress response.");
      return data;
    },
    refetchInterval: q => q.state.data?.slots?.some(busy) ? 4000 : false,
  });
  const previous = useRef<{ origin: string; states: Record<string, string> }>();
  const states = Object.fromEntries(
    query.data?.slots?.map(slot => [
      slot.binding,
      JSON.stringify([slot.status, slot.url, slot.error, slot.attempt]),
    ]) ?? [],
  );
  const statesKey = JSON.stringify(states);
  useEffect(() => {
    if (!query.data) return;
    const old = previous.current;
    previous.current = { origin, states };
    if (query.data.slots.some(slot => (!old || old.origin !== origin || old.states[slot.binding] !== states[slot.binding]) &&
      ["ready", "failed", "blocked", "ambiguous"].includes(slot.status)))
      void queryClient.invalidateQueries({ queryKey: [`${url}/inspection`] });
    // Refresh the canonical page only at a terminal/result change, not every
    // polling tick. Do not reset unsaved editor text during ordinary progress.
    if (old?.origin === origin && query.data?.slots.some(slot =>
      old.states[slot.binding] !== states[slot.binding] &&
      ["ready", "failed", "blocked", "ambiguous"].includes(slot.status),
    ))
      refreshRef.current();
  }, [origin, statesKey, query.data, queryClient, url]);
  const retry = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `${url}/__failed/retry`, {
        attempts: query.data!.slots.filter(s => s.status === "failed")
          .map(s => ({ binding: s.binding, attempt: s.attempt })),
      });
    },
    onSuccess: async () => {
      if (originRef.current !== origin) return;
      await query.refetch();
    },
    onError: () => { retriedState.current = null; },
    onSettled: () => { retrying.current = false; },
  });
  if (query.isError) return <p role="alert">Image progress is unavailable. Reload to check the saved slots.</p>;
  if (!query.data?.slots.length) return null;
  const slots = query.data.slots;
  const ready = slots.filter(s => s.status === "ready").length;
  const failed = slots.filter(s => s.status === "failed").length;
  const blocked = slots.filter(s => s.status === "blocked").length;
  const uncertain = slots.filter(s => s.status === "ambiguous").length;
  const active = slots.filter(busy).length;
  return (
    <section aria-label="Home image generation" className="space-y-3 rounded-lg border p-4" data-testid="smb-home-image-progress">
      <h3 className="font-medium">Home images</h3>
      <p className="text-sm text-muted-foreground">Generated images are illustrations, not verified photos of this business. Supplied photos and logos are preserved.</p>
      <p role="status" aria-live="polite" className="text-sm">{ready} of {slots.length} images ready; {active} in progress; {failed} failed; {blocked} blocked; {uncertain} uncertain.</p>
      {blocked > 0 && <p role="alert" className="text-sm">Image configuration needs attention. No blocked image is counted as repaired.</p>}
      {uncertain > 0 && <p role="status" className="text-sm">Uncertain submissions require operator inspection and will not be resubmitted.</p>}
      {slots.some(s => s.status === "submitted") && <p className="text-sm">Accepted results are retained for recovery without a new paid render.</p>}
      {failed > 0 && <Button type="button" size="sm" variant="outline" disabled={retry.isPending || active > 0 || retriedState.current === `${origin}:${statesKey}`}
        onClick={() => {
          const key = `${origin}:${statesKey}`;
          if (!retrying.current && retriedState.current !== key) {
            retrying.current = true; retriedState.current = key; retry.mutate();
          }
        }}>Retry failed images</Button>}
      {retry.isError && <p role="alert">{retry.error.message || "This image cannot be retried right now."}</p>}
    </section>
  );
}
